import React, { useState } from 'react';
import { adminAPI } from '../services/api';

interface Store {
  _id: string;
  name: string;
  category?: string;
  address: string;
  phone: string;
  email: string;
  isActive: boolean;
}

interface StoresManagementProps {
  stores: Store[];
  onRefresh: () => void;
}

const StoresManagement: React.FC<StoresManagementProps> = ({ stores, onRefresh }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    location: {
      address: '',
      city: 'Bhadrachalam',
      state: 'Telangana',
      pincode: '',
      coordinates: {
        latitude: 17.6688,
        longitude: 80.8936
      }
    },
    phone: '',
    email: '',
    ownerName: '',
    isActive: true,
    isOpen: true
  });

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminAPI.createStore(formData);
      alert('Store added successfully');
      setShowAddForm(false);
      setFormData({ 
        name: '', 
        category: '',
        location: {
          address: '',
          city: 'Bhadrachalam',
          state: 'Telangana',
          pincode: '',
          coordinates: { latitude: 17.6688, longitude: 80.8936 }
        },
        phone: '', 
        email: '', 
        ownerName: '',
        isActive: true,
        isOpen: true
      });
      onRefresh();
    } catch (error) {
      alert('Failed to add store');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this store?')) {
      try {
        await adminAPI.deleteStore(id);
        alert('Store deleted successfully');
        onRefresh();
      } catch (error) {
        alert('Failed to delete store');
      }
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await adminAPI.updateStore(id, { isActive: true });
      alert('Store approved successfully');
      onRefresh();
    } catch (error) {
      alert('Failed to approve store');
    }
  };

  const handleReject = async (id: string) => {
    if (window.confirm('Are you sure you want to reject this store?')) {
      try {
        await adminAPI.deleteStore(id);
        alert('Store rejected and removed');
        onRefresh();
      } catch (error) {
        alert('Failed to reject store');
      }
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Stores Management</h2>
        <button className="add-btn" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? 'Cancel' : '+ Add New Store'}
        </button>
      </div>

      {showAddForm && (
        <div style={{ background: 'white', padding: '30px', borderRadius: '15px', marginBottom: '20px' }}>
          <h3>Add New Store</h3>
          <form onSubmit={handleAdd} style={{ display: 'grid', gap: '15px', marginTop: '20px' }}>
            <input
              type="text"
              placeholder="Store Name *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #ddd' }}
            />
            <input
              type="text"
              placeholder="Owner Name *"
              value={formData.ownerName}
              onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
              required
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #ddd' }}
            />
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #ddd', color: formData.category ? '#333' : '#999' }}
            >
              <option value="" disabled>Select Store Category *</option>
              <option value="grocery">🛒 Grocery Store</option>
              <option value="pharmacy">💊 Pharmacy</option>
              <option value="vegetables">🥬 Vegetables & Fruits</option>
              <option value="meat">🥩 Meat & Seafood</option>
              <option value="bakery">🍞 Bakery Items</option>
              <option value="dairy">🥛 Dairy Products</option>
              <option value="restaurant">🍽️ Restaurant</option>
              <option value="sweets">🍬 Sweets & Confectionery</option>
              <option value="beverages">🥤 Beverages</option>
              <option value="petcare">🐾 Pet Care</option>
              <option value="stationery">📚 Stationery</option>
              <option value="flowers">🌸 Flowers & Gifts</option>
              <option value="electronics">📱 Electronics</option>
              <option value="hardware">🔧 Hardware</option>
              <option value="general">🏪 General Store</option>
            </select>
            <input
              type="text"
              placeholder="Address *"
              value={formData.location.address}
              onChange={(e) => setFormData({ ...formData, location: { ...formData.location, address: e.target.value }})}
              required
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #ddd' }}
            />
            <input
              type="text"
              placeholder="Pincode"
              value={formData.location.pincode}
              onChange={(e) => setFormData({ ...formData, location: { ...formData.location, pincode: e.target.value }})}
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #ddd' }}
            />
            <input
              type="tel"
              placeholder="Phone (10 digits) *"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              required
              maxLength={10}
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #ddd' }}
            />
            <input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              style={{ padding: '12px', borderRadius: '8px', border: '1px solid #ddd' }}
            />
            <button type="submit" className="add-btn" style={{ width: '100%' }}>
              Add Store
            </button>
          </form>
        </div>
      )}

      {stores.length === 0 ? (
        <div className="empty-state">
          <h3>No Stores Found</h3>
          <p>There are no stores in the system. Add one to get started.</p>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Address</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stores.map((store) => (
                <tr key={store._id}>
                  <td>{store.name}</td>
                  <td>
                    <span style={{ textTransform: 'capitalize' }}>
                      {store.category || 'N/A'}
                    </span>
                  </td>
                  <td>{store.address}</td>
                  <td>{store.phone}</td>
                  <td>{store.email}</td>
                  <td>
                    <span className={`status-badge ${store.isActive ? 'status-delivered' : 'status-pending'}`}>
                      {store.isActive ? 'Active' : 'Pending Approval'}
                    </span>
                  </td>
                  <td>
                    {!store.isActive ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="action-btn" onClick={() => handleApprove(store._id)} style={{ background: '#22c55e' }}>
                          ✓ Approve
                        </button>
                        <button className="action-btn delete-btn" onClick={() => handleReject(store._id)}>
                          ✗ Reject
                        </button>
                      </div>
                    ) : (
                      <button className="action-btn delete-btn" onClick={() => handleDelete(store._id)}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StoresManagement;
